import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { MessageCircle, Phone, Wifi, WifiOff, QrCode, Loader2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { whatsappAPI } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';

interface InstanceData {
  instance: string;
  status: string;
  connected: boolean;
  phone?: string;
}

interface InstanceDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  instance: {
    name: string;
    key: 'saulo' | 'robo' | 'gabriel' | 'promocoes';
    data?: InstanceData;
  };
}

// Mapeamento de instâncias para números de telefone
const INSTANCE_PHONES: Record<'saulo' | 'robo' | 'gabriel' | 'promocoes', string> = {
  saulo: '5513978240168',
  robo: '5512992035128',
  gabriel: '5512978980610',
  promocoes: '5512936180263',
};

export function InstanceDetailsModal({ isOpen, onClose, instance }: InstanceDetailsModalProps) {
  const queryClient = useQueryClient();
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [pairCode, setPairCode] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  // Buscar número automático da instância
  const instancePhone = INSTANCE_PHONES[instance.key] || '';

  const connectMutation = useMutation({
    mutationFn: async (phone?: string) => {
      const instanceMap = {
        saulo: 'OPERACIONAL_SAULO',
        robo: 'OPERACIONAL_ROBO',
        gabriel: 'COMERCIAL_GABRIEL',
        promocoes: 'PROMOCOES',
      } as const;

      return await whatsappAPI.connectInstance(instanceMap[instance.key], phone);
    },
    onSuccess: (response) => {
      if (response.data.qrcode) {
        setQrCode(response.data.qrcode);
        setPairCode(null);
        toast.success('QR Code gerado! Escaneie com o WhatsApp');
      } else if (response.data.paircode) {
        setPairCode(response.data.paircode);
        setQrCode(null);
        toast.success('Código de pareamento gerado!');
      } else {
        toast.success('Instância conectando...');
      }

      // Atualizar status após 3 segundos
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['whatsapp-instances'] });
      }, 3000);
    },
    onError: (error: unknown) => {
      const errorMessage =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      toast.error(errorMessage || 'Erro ao conectar instância');
    },
  });

  const handleConnectQR = () => {
    setQrCode(null);
    setPairCode(null);
    connectMutation.mutate(undefined);
  };

  const handleConnectPair = () => {
    if (!instancePhone) {
      toast.error('Número de telefone não configurado para esta instância');
      return;
    }

    setQrCode(null);
    setPairCode(null);
    connectMutation.mutate(instancePhone);
  };

  // Polling effect - atualizar status automaticamente quando estiver connecting
  useEffect(() => {
    const isConnecting =
      instance.data?.status === 'connecting' || (qrCode !== null || pairCode !== null);

    if (!isOpen || !isConnecting) {
      setIsPolling(false);
      return;
    }

    setIsPolling(true);

    const pollInterval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-instances'] });
    }, 3000); // Poll a cada 3 segundos

    return () => {
      clearInterval(pollInterval);
      setIsPolling(false);
    };
  }, [isOpen, instance.data?.status, qrCode, pairCode, queryClient]);

  // Detectar quando conectou e limpar QR code
  useEffect(() => {
    if (instance.data?.connected) {
      setQrCode(null);
      setPairCode(null);
      setIsPolling(false);
      toast.success(`${instance.name} conectada com sucesso!`);
    }
  }, [instance.data?.connected, instance.name]);

  const getStatusColor = () => {
    if (!instance.data) return 'default';
    if (instance.data.connected) return 'success';
    if (instance.data.status === 'connecting') return 'warning';
    return 'error';
  };

  const getStatusIcon = () => {
    if (!instance.data) return <WifiOff className="h-4 w-4" />;
    return instance.data.connected ? (
      <Wifi className="h-4 w-4" />
    ) : (
      <WifiOff className="h-4 w-4" />
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              Instância WhatsApp - {instance.name}
            </div>
          </DialogTitle>
          <DialogDescription>
            Gerencie a conexão da instância do WhatsApp
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 space-y-4">
          {/* Status atual */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Status:</span>
            <Badge variant={getStatusColor()} className="flex items-center gap-1.5">
              {isPolling && <Loader2 className="h-3 w-3 animate-spin" />}
              {!isPolling && getStatusIcon()}
              {instance.data?.connected
                ? 'Online'
                : instance.data?.status === 'connecting'
                  ? 'Conectando...'
                  : 'Offline'}
            </Badge>
          </div>

          {/* Telefone conectado */}
          {instance.data?.phone && (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Telefone:</span>
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Phone className="h-3.5 w-3.5" />
                {instance.data.phone}
              </div>
            </div>
          )}

          {/* Status técnico */}
          {instance.data?.status && (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Status Técnico:</span>
              <code className="text-xs bg-muted px-2 py-1 rounded">
                {instance.data.status}
              </code>
            </div>
          )}

          {/* QR Code */}
          {qrCode && (
            <div className="border rounded-lg p-4 bg-white dark:bg-muted flex flex-col items-center gap-2">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <QrCode className="h-4 w-4" />
                Escaneie com o WhatsApp
              </div>
              <img
                src={qrCode}
                alt="QR Code para conexão"
                className="w-64 h-64"
              />
              <p className="text-xs text-center text-muted-foreground max-w-xs">
                Abra o WhatsApp no celular, vá em Mais opções → Aparelhos conectados → Conectar aparelho
              </p>
            </div>
          )}

          {/* Código de Pareamento */}
          {pairCode && (
            <div className="border rounded-lg p-4 bg-white dark:bg-muted flex flex-col items-center gap-3">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Phone className="h-4 w-4" />
                Código de Pareamento
              </div>
              <div className="text-4xl font-bold tracking-wider font-mono bg-muted px-6 py-3 rounded">
                {pairCode}
              </div>
              <p className="text-xs text-center text-muted-foreground max-w-xs">
                Abra o WhatsApp no celular, vá em Aparelhos conectados → Conectar aparelho → Digite o código acima
              </p>
            </div>
          )}

          {/* Mensagem de aguardando conexão */}
          {!instance.data?.connected && instance.data?.status === 'connecting' && !qrCode && !pairCode && (
            <div className="text-center py-3">
              <p className="text-sm text-yellow-600 dark:text-yellow-500 font-medium flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Aguardando leitura do código...
              </p>
            </div>
          )}

          {/* Mensagem de sucesso quando conectado */}
          {instance.data?.connected && (
            <div className="text-center py-3">
              <p className="text-sm text-green-600 dark:text-green-500 font-medium">
                ✓ Instância conectada e funcionando
              </p>
            </div>
          )}
        </div>

        {/* Opções de conexão quando não conectado */}
        {!instance.data?.connected && (
          <div className="px-6 pb-4 space-y-3">
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Escolha o método de conexão:</p>

              <div className="grid grid-cols-2 gap-3">
                {/* Método 1: QR Code */}
                <Button
                  onClick={handleConnectQR}
                  disabled={connectMutation.isPending}
                  variant={qrCode ? 'outline' : 'default'}
                  className="w-full"
                >
                  {connectMutation.isPending && !instancePhone && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  <QrCode className="mr-2 h-4 w-4" />
                  {qrCode ? 'Novo QR' : 'QR Code'}
                </Button>

                {/* Método 2: Código de Pareamento */}
                <Button
                  onClick={handleConnectPair}
                  disabled={connectMutation.isPending || !instancePhone}
                  variant={pairCode ? 'outline' : 'default'}
                  className="w-full"
                >
                  {connectMutation.isPending && instancePhone && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  <Phone className="mr-2 h-4 w-4" />
                  {pairCode ? 'Novo Código' : 'Pareamento'}
                </Button>
              </div>

              {instancePhone && (
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Pareamento usa o número: {instancePhone.replace(/(\d{2})(\d{2})(\d{5})(\d{4})/, '+$1 ($2) $3-$4')}
                </p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
